/**
 * ObjectEditPage React component
 * 
 * @author    Jens Gruschel
 * @copyright © 2018 contexagon GmbH
 */


// react
import React from 'react';
import connect from "react-redux/es/connect/connect";

// material UI
import {withStyles} from "@material-ui/core";

// material UI components
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button'
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';

// icons
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

// application components
import ObjectEditPanel from "./ObjectEditPanel";
import ObjectHeaderPanel from "./ObjectHeaderPanel";
import ObjectList from "../listing/ObjectList";

// application
import {selectObjectById, selectOriginalObjectById, selectCurrentObjectId, selectCurrentReferenceFieldPath,
    selectCurrentElementFieldPath, selectCurrentReadOnly, selectCurrentFlags} from "../../redux/editing/selectors";
import {saveObject, pushAndEditObject, popAndAddReference, duplicateObject,
    saveAndAssignReference, assignElement, cancelWithoutSaving} from "../../operations/editing";
import {deleteObject, searchTag} from "../../operations/listing";
import {changeUserSetting} from "../../operations/configuration";
import {selectUserSetting} from "../../redux/configuration/selectors";
import {setEditFlagAction} from "../../redux/editing/actions";


/**
 * ObjectEditPage shows an editor for a single object
 */
export class ObjectEditPage extends React.Component {

    handlePanelChange = panelId => (event, expanded) => {
        this.props.setEditFlagAction("expandedPanelId", expanded ? panelId : this.props.expandedPanelId === 'selection' ? 'creation' : 'selection');
    };

    render() {

        const { classes, expandedPanelId } = this.props;

        if (this.props.referenceFieldPath) {
            return (
                <div>
                    {this.renderSuperObjects()}
                    <ExpansionPanel style={{marginTop: 0}} expanded={expandedPanelId === 'creation'} onChange={this.handlePanelChange('creation')}>
                        <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h6" className={classes.panelTitle}>neues Objekt erzeugen</Typography>
                        </ExpansionPanelSummary>
                        <ExpansionPanelDetails className={classes.detailsPanel}>
                            {this.renderEditPanel()}
                        </ExpansionPanelDetails>
                    </ExpansionPanel>
                    <ExpansionPanel expanded={expandedPanelId === 'selection'} onChange={this.handlePanelChange('selection')}>
                        <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h6" className={classes.panelTitle}>Auswahl vorhandener Objekte</Typography>
                        </ExpansionPanelSummary>
                        <ExpansionPanelDetails className={classes.detailsPanel}>
                            <div>
                                <div className={classes.selectionGrid}>
                                    <ObjectList
                                        items={this.props.referencableObjects}
                                        readOnly={this.props.readOnly}
                                        representation={this.props.representation}
                                        onChangeRepresentation={value => this.props.changeUserSetting("listRepresentation-selection", value)}
                                        onSelect={(index, object) => this.props.popAndAddReference(object.id)}
                                        onEdit={(index, object) => this.props.pushAndEditObject(object.id, this.props.readOnly)}
                                        /*onDelete={(index, object) => this.props.deleteObject(object.id)}*/
                                        onSearchTag={this.props.searchTag}
                                    />
                                </div>
                                <div className={classes.buttons}>
                                    <Button
                                        className={classes.button}
                                        variant="contained"
                                        onClick={() => this.props.cancelWithoutSaving()}
                                    >
                                        cancel
                                    </Button>
                                </div>
                            </div>
                        </ExpansionPanelDetails>
                    </ExpansionPanel>
                </div>
            );
        }
        else {
            return (
                <div>
                    {this.renderSuperObjects()}
                    <Paper className={classes.panel} elevation={1}>
                        {this.renderEditPanel()}
                    </Paper>
                </div>
            );
        }
    }

    renderSuperObjects() {

        const levelCount = this.props.superObjects.length;

        return this.props.superObjects.map((object, index) => {
            if (object) {
                return <ObjectHeaderPanel
                    key={index}
                    objectId={object.id}
                    objectType={object.type}
                    object={object}
                    modified={this.props.modified[object.id]}
                    level={levelCount - index}
                />;
            }
            else {
                return null;
            }
        });
    }

    renderEditPanel() {

        const onSaveObject = (object, close) => {
            if (!close) {
                this.props.saveObject(object, false);
            }
            else if (this.props.referenceFieldPath) {
                this.props.saveAndAssignReference(object); // always closes
            }
            else if (this.props.elementFieldPath) {
                this.props.assignElement(object); // always closes
            }
            else {
                this.props.saveObject(object, true);
            }
        };

        return (
            <ObjectEditPanel
                objectId={this.props.objectId}
                objectType={this.props.object.type}
                object={this.props.object}
                originalObject={this.props.originalObject}
                modified={this.props.modified[this.props.objectId]}
                readOnly={this.props.readOnly}
                onSaveObject={onSaveObject}
                onCancel={this.props.cancelWithoutSaving}
                isElementField={!!this.props.elementFieldPath}
            />
        );
    }
}

const styles = {
    panel: {
        padding: "10px 0 10px 0",
        marginBottom: 30
    },
    selectionGrid: {
        marginLeft: 60,
        marginRight: 60,
        marginTop: 20,
        marginBottom: 20
    },
    title: {
        margin: "auto",
        textAlign: "center",
        overflow: 'hidden',
        whiteSpace: "nowrap",
        textOverflow: 'ellipsis',
        maxWidth: 350
    },
    buttons: {
        margin: "10px 80px",
        textAlign: "right"
    },
    button: {
        margin: "0px 0px 0px 20px"
    },
    detailsPanel: {
        display: "block",
        padding: 0
    },
    panelTitle: {
        paddingLeft: 32,
        paddingRight: 32,
        margin: "auto",
        textAlign: "center",
        overflow: 'hidden',
        whiteSpace: "nowrap",
        textOverflow: 'ellipsis',
        maxWidth: 350
    }
};

const mapStateToProps = state => {

    const editStack = state.editing.editStack;
    const superObjects = editStack.slice(0, -1).map(item => item.objectId).map(id => selectObjectById(state.editing, id));
    const objectId = selectCurrentObjectId(state.editing);
    const flags = selectCurrentFlags(state.editing);

    return {
        objectId: objectId,
        object: selectObjectById(state.editing, objectId),
        originalObject: selectOriginalObjectById(state.editing, objectId),
        modified: state.editing.modified || {},
        readOnly: selectCurrentReadOnly(state.editing),
        referenceFieldPath: selectCurrentReferenceFieldPath(state.editing),
        elementFieldPath: selectCurrentElementFieldPath(state.editing),
        referencableObjects: state.editing.referencableObjects,
        superObjects: superObjects,
        preferSelection: (flags && flags.preference !== "creation" && state.editing.referencableObjects) ? true : false,
        representation: selectUserSetting(state.configuration, "listRepresentation-selection"),
        expandedPanelId: flags.expandedPanelId || flags.preference || "selection"
    }
};

export default connect(
    mapStateToProps,
    {
        saveObject, pushAndEditObject, popAndAddReference, saveAndAssignReference, assignElement,
        cancelWithoutSaving, deleteObject, searchTag, duplicateObject, changeUserSetting, setEditFlagAction
    }
)(withStyles(styles)(ObjectEditPage));
